import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  Plus, Edit, Trash2, Eye, DollarSign, Star, Calendar, 
  MapPin, Clock, Users, CheckCircle, XCircle, Loader2,
  TrendingUp, Award, Compass, Heart, User, Settings, Map,
  AlertTriangle, Play, Flag, CreditCard, FileCheck, Gift,
  ShieldCheck, Upload, BadgeCheck, Camera, Briefcase, BarChart3,
  Zap, Link2, CalendarDays
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import { experiencesApi, bookingsApi, statsApi, favoritesApi, bookingsApiExtended, reviewsApi } from '@/api';
import { StarRating, InteractiveStarRating } from '@/components/StarRating';
import { ExperienceCard } from '@/components/ExperienceCard';
import ExplorerProfileModal from '@/components/ExplorerProfileModal';
import AvailabilityToggle from '@/components/AvailabilityToggle';
import StripeConnectCard from '@/components/StripeConnectCard';
import CharterAcceptanceModal from '@/components/CharterAcceptanceModal';
import { SubscriptionCard } from '@/components/SubscriptionCard';
import GuideServicesManager from '@/components/GuideServicesManager';
import GuideAnalyticsDashboard from '@/components/GuideAnalyticsDashboard';
import EventPromotionModal from '@/components/EventPromotionModal';
import LinkEventsModal from '@/components/LinkEventsModal';
import GuideVerificationCard from '@/components/GuideVerificationCard';
import GuideAgenda from '@/components/dashboard/GuideAgenda';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';

const DashboardPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAuthenticated, switchRole, refreshUser } = useAuth();
  
  const [stats, setStats] = useState(null);
  const [experiences, setExperiences] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showCharterModal, setShowCharterModal] = useState(false);
  const [showPromotionModal, setShowPromotionModal] = useState(false);
  const [showLinkEventsModal, setShowLinkEventsModal] = useState(false);
  const [selectedEventForPromotion, setSelectedEventForPromotion] = useState(null);
  const [linkedEventIds, setLinkedEventIds] = useState([]);
  const [activeTab, setActiveTab] = useState('bookings');

  // Handle URL params for deep linking (e.g., ?tab=experiences&action=create)
  useEffect(() => {
    const tab = searchParams.get('tab');
    const action = searchParams.get('action');
    
    if (tab) {
      setActiveTab(tab);
    }
    
    if (action === 'create' && user?.role === 'explorer') {
      // Small delay to ensure the tab is switched first
      setTimeout(() => {
        setShowCreateModal(true);
        // Clear the action param to prevent reopening on refresh
        searchParams.delete('action');
        setSearchParams(searchParams, { replace: true });
      }, 500);
    }
  }, [searchParams, user?.role]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
    
    // Show profile modal for new explorers with incomplete profile
    if (user?.role === 'explorer' && !user?.profile_complete) {
      setShowProfileModal(true);
    }
  }, [isAuthenticated, user?.role, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [bookingsData, statsData, favoritesData] = await Promise.all([
        bookingsApi.getAll(),
        user?.role === 'explorer' 
          ? statsApi.getExplorerStats()
          : statsApi.getExperimenterStats(),
        user?.role === 'experimenter' ? favoritesApi.getAll() : Promise.resolve([])
      ]);
      
      setBookings(bookingsData);
      setStats(statsData);
      setFavorites(favoritesData);
      
      if (user?.role === 'explorer') {
        const expData = await experiencesApi.getAll({ limit: 100 });
        setExperiences(expData.experiences.filter(e => e.explorer_id === user.user_id));
        
        // Fetch linked events for the guide
        setLinkedEventIds(user?.linked_events || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePromoteEvent = (event) => {
    setSelectedEventForPromotion(event);
    setShowPromotionModal(true);
  };

  const handleEventsUpdated = async () => {
    await refreshUser();
    setLinkedEventIds(user?.linked_events || []);
  };

  const handleSwitchRole = async () => {
    const newRole = user.role === 'explorer' ? 'experimenter' : 'explorer';
    try {
      await switchRole(newRole);
      toast.success(`Vous êtes maintenant ${newRole === 'explorer' ? 'explorateur' : 'expérimentateur'}`);
      fetchData();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleUpdateBookingStatus = async (bookingId, status) => {
    try {
      await bookingsApi.updateStatus(bookingId, status);
      toast.success('Statut mis à jour');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <main className="min-h-screen pt-20 pb-12" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 py-8">
          <div className="flex items-center gap-4">
            {/* Profile Avatar */}
            <div className="relative">
              <img 
                src={user?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'U')}&background=f97316&color=fff&size=64`}
                alt={user?.name}
                className="w-16 h-16 rounded-full object-cover ring-4 ring-primary/20"
              />
              {user?.role === 'explorer' && user?.profile_complete && (
                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center border-2 border-white">
                  <CheckCircle className="w-3 h-3 text-white" />
                </div>
              )}
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">{t('dashboard.title')}</h1>
              <p className="text-muted-foreground mt-1">
                {i18n.language === 'fr' ? 'Bienvenue' : 'Welcome'}, {user?.name}
                {user?.role === 'explorer' && !user?.profile_complete && (
                  <span className="ml-2 text-orange-500 text-sm">
                    ({i18n.language === 'fr' ? 'Profil incomplet' : 'Profile incomplete'})
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            {user?.role === 'explorer' && (
              <Button 
                variant="outline" 
                onClick={() => setShowProfileModal(true)} 
                data-testid="edit-profile-btn"
              >
                <Settings className="w-5 h-5 mr-2" />
                {i18n.language === 'fr' ? 'Mon profil' : 'My profile'}
              </Button>
            )}
            <Button variant="outline" onClick={handleSwitchRole} data-testid="switch-role-btn">
              {user?.role === 'explorer' 
                ? (i18n.language === 'fr' ? 'Mode voyageur' : 'Traveler mode')
                : (i18n.language === 'fr' ? 'Mode guide' : 'Guide mode')
              }
            </Button>
            {user?.role === 'explorer' && (
              <Button 
                onClick={() => {
                  if (!user?.charter_accepted) {
                    setShowCharterModal(true);
                  } else {
                    setShowCreateModal(true);
                  }
                }} 
                data-testid="create-experience-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                {t('dashboard.create_experience')}
              </Button>
            )}
          </div>
        </div>

        {/* Profile Completion Banner for Explorers */}
        {user?.role === 'explorer' && !user?.profile_complete && (
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="mb-6 p-4 bg-gradient-to-r from-orange-100 to-orange-50 rounded-2xl border border-orange-200"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {i18n.language === 'fr' ? 'Complétez votre profil d\'explorateur' : 'Complete your explorer profile'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {i18n.language === 'fr' 
                      ? 'Un profil complet augmente vos chances d\'être contacté par des voyageurs !'
                      : 'A complete profile increases your chances of being contacted by travelers!'}
                  </p>
                </div>
              </div>
              <Button onClick={() => setShowProfileModal(true)} size="sm">
                {i18n.language === 'fr' ? 'Compléter' : 'Complete'}
              </Button>
            </div>
          </motion.div>
        )}

        {/* Charter Acceptance Banner for Explorers */}
        {user?.role === 'explorer' && !user?.charter_accepted && (
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="mb-6 p-4 bg-gradient-to-r from-yellow-100 to-amber-50 rounded-2xl border border-yellow-300"
            data-testid="charter-banner"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-yellow-200 flex items-center justify-center">
                  <FileCheck className="w-5 h-5 text-yellow-700" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {i18n.language === 'fr' ? 'Acceptez la Charte des Guides' : 'Accept the Guide Charter'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {i18n.language === 'fr' 
                      ? 'Vous devez accepter la charte pour créer des expériences et recevoir des réservations.'
                      : 'You must accept the charter to create experiences and receive bookings.'}
                  </p>
                </div>
              </div>
              <Button 
                onClick={() => setShowCharterModal(true)} 
                size="sm"
                className="bg-yellow-600 hover:bg-yellow-700"
                data-testid="accept-charter-banner-btn"
              >
                <FileCheck className="w-4 h-4 mr-2" />
                {i18n.language === 'fr' ? 'Accepter' : 'Accept'}
              </Button>
            </div>
          </motion.div>
        )}

        {/* Availability Toggle for Explorers */}
        {user?.role === 'explorer' && (
          <div className="mb-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <AvailabilityToggle user={user} onUpdate={refreshUser} />
            <StripeConnectCard user={user} />
            <GuideVerificationCard user={user} onVerificationRequested={refreshUser} />
          </div>
        )}

        {/* Map Button and Subscription for Experimenters (Travelers) */}
        {user?.role === 'experimenter' && (
          <>
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-gradient-to-r from-primary to-orange-400 rounded-2xl"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-white">
                  <Map className="w-8 h-8" />
                  <div>
                    <h3 className="font-bold text-lg">
                      {i18n.language === 'fr' ? 'Carte des guides' : 'Guides Map'}
                    </h3>
                    <p className="text-white/80 text-sm">
                      {i18n.language === 'fr' 
                        ? 'Trouvez des guides disponibles près de vous'
                        : 'Find available guides near you'}
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate('/map')}
                  className="bg-white text-primary hover:bg-white/90"
                >
                  {i18n.language === 'fr' ? 'Voir la carte' : 'View map'}
                </Button>
              </div>
            </motion.div>
            
            {/* Subscription Card for Travelers - Access to free experiences */}
            <div className="mb-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
              <SubscriptionCard user={user} />
            </div>
          </>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {user?.role === 'explorer' ? (
            <>
              <StatCard 
                icon={DollarSign} 
                value={`${stats?.total_earnings || 0}€`} 
                label={t('dashboard.earnings')} 
                color="text-green-600"
              />
              <StatCard 
                icon={Calendar} 
                value={stats?.total_bookings || 0} 
                label={t('dashboard.total_bookings')} 
                color="text-blue-600"
              />
              <StatCard 
                icon={Star} 
                value={stats?.rating?.toFixed(1) || '0.0'} 
                label={t('dashboard.rating')} 
                color="text-yellow-600"
              />
              <StatCard 
                icon={Award} 
                value={stats?.completed_bookings || 0} 
                label="Terminées" 
                color="text-purple-600"
              />
            </>
          ) : (
            <>
              <StatCard 
                icon={Compass} 
                value={stats?.completed_trips || 0} 
                label={t('dashboard.trips')} 
                color="text-primary"
              />
              <StatCard 
                icon={Calendar} 
                value={stats?.total_bookings || 0} 
                label={t('dashboard.total_bookings')} 
                color="text-blue-600"
              />
              <StatCard 
                icon={Heart} 
                value={favorites.length || 0} 
                label={i18n.language === 'fr' ? 'Favoris' : 'Favorites'} 
                color="text-red-500"
              />
              <StatCard 
                icon={Star} 
                value={stats?.reviews_given || 0} 
                label={i18n.language === 'fr' ? 'Avis donnés' : 'Reviews given'} 
                color="text-yellow-600"
              />
            </>
          )}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="bookings" data-testid="bookings-tab">
              {t('dashboard.my_bookings')}
            </TabsTrigger>
            {user?.role === 'explorer' && (
              <TabsTrigger value="experiences" data-testid="experiences-tab">
                {t('dashboard.my_experiences')}
              </TabsTrigger>
            )}
            {user?.role === 'explorer' && (
              <TabsTrigger value="services" data-testid="services-tab">
                <Briefcase className="w-4 h-4 mr-2" />
                {i18n.language === 'fr' ? 'Mes services' : 'My Services'}
              </TabsTrigger>
            )}
            {user?.role === 'explorer' && (
              <TabsTrigger value="analytics" data-testid="analytics-tab">
                <BarChart3 className="w-4 h-4 mr-2" />
                {i18n.language === 'fr' ? 'Analytics' : 'Analytics'}
              </TabsTrigger>
            )}
            {user?.role === 'explorer' && (
              <TabsTrigger value="local-events" data-testid="local-events-tab">
                <CalendarDays className="w-4 h-4 mr-2" />
                {i18n.language === 'fr' ? 'Événements' : 'Events'}
              </TabsTrigger>
            )}
            {user?.role === 'explorer' && (
              <TabsTrigger value="agenda" data-testid="agenda-tab">
                <Calendar className="w-4 h-4 mr-2" />
                {i18n.language === 'fr' ? 'Agenda' : 'Agenda'}
              </TabsTrigger>
            )}
            {user?.role === 'experimenter' && (
              <TabsTrigger value="favorites" data-testid="favorites-tab">
                <Heart className="w-4 h-4 mr-2" />
                {i18n.language === 'fr' ? 'Favoris' : 'Favorites'}
              </TabsTrigger>
            )}
          </TabsList>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            {bookings.length === 0 ? (
              <div className="text-center py-12 bg-card rounded-2xl border">
                <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  {i18n.language === 'fr' ? 'Aucune réservation' : 'No bookings yet'}
                </p>
                {user?.role === 'experimenter' && (
                  <Link to="/explore">
                    <Button className="mt-4">{t('nav.explore')}</Button>
                  </Link>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {bookings.map((booking) => (
                  <BookingCard 
                    key={booking.booking_id} 
                    booking={booking}
                    isExplorer={user?.role === 'explorer'}
                    onUpdateStatus={handleUpdateBookingStatus}
                    onRefresh={fetchData}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Favorites Tab (Experimenter only) */}
          {user?.role === 'experimenter' && (
            <TabsContent value="favorites">
              {favorites.length === 0 ? (
                <div className="text-center py-12 bg-card rounded-2xl border">
                  <Heart className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-4">
                    {i18n.language === 'fr' ? 'Aucun favori pour le moment' : 'No favorites yet'}
                  </p>
                  <p className="text-sm text-muted-foreground mb-4">
                    {i18n.language === 'fr' 
                      ? 'Explorez nos expériences et ajoutez vos coups de cœur ici !'
                      : 'Explore our experiences and add your favorites here!'}
                  </p>
                  <Link to="/explore">
                    <Button>{t('nav.explore')}</Button>
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {favorites.map((exp) => (
                    <ExperienceCard key={exp.experience_id} experience={exp} />
                  ))}
                </div>
              )}
            </TabsContent>
          )}

          {/* Experiences Tab (Explorer only) */}
          {user?.role === 'explorer' && (
            <TabsContent value="experiences">
              {experiences.length === 0 ? (
                <div className="text-center py-12 bg-card rounded-2xl border">
                  <MapPin className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-4">
                    {i18n.language === 'fr' ? 'Aucune expérience créée' : 'No experiences created'}
                  </p>
                  <Button onClick={() => setShowCreateModal(true)}>
                    <Plus className="w-5 h-5 mr-2" />
                    {t('dashboard.create_experience')}
                  </Button>
                </div>
              ) : (
                <div className="grid gap-4">
                  {experiences.map((exp) => (
                    <ExperienceRow key={exp.experience_id} experience={exp} onRefresh={fetchData} />
                  ))}
                </div>
              )}
            </TabsContent>
          )}

          {/* Services Tab (Explorer only) */}
          {user?.role === 'explorer' && (
            <TabsContent value="services">
              <GuideServicesManager />
            </TabsContent>
          )}

          {/* Analytics Tab (Explorer only) */}
          {user?.role === 'explorer' && (
            <TabsContent value="analytics">
              <GuideAnalyticsDashboard />
            </TabsContent>
          )}

          {/* Local Events Tab (Explorer only) */}
          {user?.role === 'explorer' && (
            <TabsContent value="local-events">
              <LocalEventsTab 
                experiences={experiences}
                linkedEventIds={linkedEventIds}
                onPromoteEvent={handlePromoteEvent}
                onOpenLinkModal={() => setShowLinkEventsModal(true)}
              />
            </TabsContent>
          )}

          {/* Agenda Tab (Explorer only) */}
          {user?.role === 'explorer' && (
            <TabsContent value="agenda">
              <GuideAgenda userId={user?.user_id} />
            </TabsContent>
          )}
        </Tabs>

        {/* Create Experience Modal */}
        <CreateExperienceModal 
          open={showCreateModal} 
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchData();
          }}
        />

        {/* Explorer Profile Modal */}
        {user?.role === 'explorer' && (
          <ExplorerProfileModal
            open={showProfileModal}
            onClose={() => setShowProfileModal(false)}
            user={user}
            onSuccess={() => {
              setShowProfileModal(false);
              refreshUser();
              fetchData();
            }}
          />
        )}

        {/* Charter Acceptance Modal */}
        {user?.role === 'explorer' && (
          <CharterAcceptanceModal
            isOpen={showCharterModal}
            onClose={() => setShowCharterModal(false)}
            user={user}
            onAccepted={() => {
              setShowCharterModal(false);
              refreshUser();
              toast.success(i18n.language === 'fr' 
                ? 'Charte acceptée ! Vous pouvez maintenant créer des expériences.'
                : 'Charter accepted! You can now create experiences.');
            }}
          />
        )}

        {/* Event Promotion Modal */}
        {selectedEventForPromotion && (
          <EventPromotionModal
            event={selectedEventForPromotion}
            isOpen={showPromotionModal}
            onClose={() => {
              setShowPromotionModal(false);
              setSelectedEventForPromotion(null);
            }}
            onSuccess={() => {
              setShowPromotionModal(false);
              setSelectedEventForPromotion(null);
              fetchData();
            }}
          />
        )}

        {/* Link Events Modal */}
        <LinkEventsModal
          isOpen={showLinkEventsModal}
          onClose={() => setShowLinkEventsModal(false)}
          linkedEventIds={linkedEventIds}
          onEventsUpdated={handleEventsUpdated}
        />
      </div>
    </main>
  );
};

const StatCard = ({ icon: Icon, value, label, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="stat-card"
  >
    <Icon className={`w-6 h-6 ${color} mb-2`} />
    <div className="value">{value}</div>
    <div className="label">{label}</div>
  </motion.div>
);

const BookingCard = ({ booking, isExplorer, onUpdateStatus, onRefresh }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    paid: 'bg-blue-100 text-blue-800',
    in_progress: 'bg-purple-100 text-purple-800',
    confirmed: 'bg-green-100 text-green-800',
    completed: 'bg-emerald-100 text-emerald-800',
    cancelled: 'bg-red-100 text-red-800',
    disputed: 'bg-orange-100 text-orange-800',
    refunded: 'bg-gray-100 text-gray-800'
  };

  const statusLabels = {
    pending: i18n.language === 'fr' ? 'En attente' : 'Pending',
    paid: i18n.language === 'fr' ? 'Payé' : 'Paid',
    in_progress: i18n.language === 'fr' ? 'En cours' : 'In progress',
    confirmed: i18n.language === 'fr' ? 'Confirmé' : 'Confirmed',
    completed: i18n.language === 'fr' ? 'Terminé' : 'Completed',
    cancelled: i18n.language === 'fr' ? 'Annulé' : 'Cancelled',
    disputed: i18n.language === 'fr' ? 'Litige' : 'Disputed',
    refunded: i18n.language === 'fr' ? 'Remboursé' : 'Refunded'
  };

  const paymentStatusLabels = {
    pending: i18n.language === 'fr' ? 'En attente' : 'Pending',
    paid: i18n.language === 'fr' ? 'Payé' : 'Paid',
    held: i18n.language === 'fr' ? 'Bloqué' : 'Held',
    released: i18n.language === 'fr' ? 'Libéré' : 'Released',
    refunded: i18n.language === 'fr' ? 'Remboursé' : 'Refunded'
  };

  const handleStartExperience = async () => {
    setActionLoading(true);
    try {
      await bookingsApiExtended.startExperience(booking.booking_id);
      toast.success(i18n.language === 'fr' ? 'Expérience démarrée !' : 'Experience started!');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCompleteExperience = async () => {
    setActionLoading(true);
    try {
      await bookingsApiExtended.completeExperience(booking.booking_id);
      toast.success(i18n.language === 'fr' 
        ? 'Expérience terminée ! Le paiement sera libéré dans 48h.' 
        : 'Experience completed! Payment will be released in 48h.');
      setShowCompleteModal(false);
      setShowReviewModal(true); // Show review modal after completion
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  // Check if booking can be reviewed (completed and not yet reviewed)
  const canReview = booking.status === 'completed' && !booking.reviewed;
  const canComplete = !isExplorer && (booking.status === 'paid' || booking.status === 'in_progress');
  const canStart = isExplorer && booking.status === 'paid';

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card rounded-xl border p-4 md:p-6" 
      data-testid={`booking-${booking.booking_id}`}
    >
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div className="flex-1">
          {/* Status badges */}
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[booking.status]}`}>
              {statusLabels[booking.status]}
            </span>
            {booking.payment_status && booking.payment_status !== 'pending' && (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                booking.payment_status === 'paid' || booking.payment_status === 'held' 
                  ? 'bg-green-100 text-green-800' 
                  : booking.payment_status === 'released' 
                    ? 'bg-emerald-100 text-emerald-800'
                    : 'bg-gray-100 text-gray-800'
              }`}>
                💳 {paymentStatusLabels[booking.payment_status]}
              </span>
            )}
          </div>
          
          <h3 className="font-semibold text-lg">{booking.experience_title}</h3>
          
          <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {new Date(booking.date).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              })}
            </span>
            <span className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              {booking.participants} {i18n.language === 'fr' ? 'pers.' : 'ppl'}
            </span>
            <span className="font-medium text-primary flex items-center gap-1">
              <DollarSign className="w-4 h-4" />
              {booking.total_price}€
            </span>
          </div>
          
          <p className="text-sm text-muted-foreground mt-1">
            {isExplorer 
              ? `👤 ${i18n.language === 'fr' ? 'Client' : 'Client'}: ${booking.experimenter_name}`
              : `🧭 ${i18n.language === 'fr' ? 'Guide' : 'Guide'}: ${booking.explorer_name}`
            }
          </p>

          {/* Payout info for explorers */}
          {isExplorer && booking.status === 'completed' && booking.payout_scheduled_at && (
            <div className="mt-2 text-xs text-green-600 bg-green-50 rounded-lg px-3 py-2 inline-flex items-center gap-2">
              <Clock className="w-3 h-3" />
              {i18n.language === 'fr' ? 'Paiement prévu:' : 'Payout scheduled:'}{' '}
              {new Date(booking.payout_scheduled_at).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US')}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          {isExplorer ? (
            <>
              {/* Explorer actions */}
              {canStart && (
                <Button 
                  size="sm" 
                  onClick={handleStartExperience}
                  disabled={actionLoading}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Play className="w-4 h-4 mr-1" /> 
                  {i18n.language === 'fr' ? 'Démarrer' : 'Start'}
                </Button>
              )}
              {booking.status === 'in_progress' && (
                <span className="text-xs text-purple-600 bg-purple-50 rounded-full px-3 py-1 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> 
                  {i18n.language === 'fr' ? 'En cours...' : 'In progress...'}
                </span>
              )}
              {booking.status !== 'cancelled' && booking.status !== 'completed' && booking.status !== 'in_progress' && (
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => onUpdateStatus(booking.booking_id, 'cancelled')}
                >
                  <XCircle className="w-4 h-4" />
                </Button>
              )}
            </>
          ) : (
            <>
              {/* Traveler actions */}
              {booking.payment_status === 'pending' && booking.status !== 'cancelled' && (
                <Button size="sm" onClick={() => navigate(`/booking/${booking.booking_id}`)}>
                  💳 {i18n.language === 'fr' ? 'Payer' : 'Pay'}
                </Button>
              )}
              
              {canComplete && (
                <Button 
                  size="sm"
                  onClick={() => setShowCompleteModal(true)}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="complete-experience-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  {i18n.language === 'fr' ? 'Terminer' : 'Complete'}
                </Button>
              )}
              
              {canReview && (
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => setShowReviewModal(true)}
                  data-testid="review-btn"
                >
                  <Star className="w-4 h-4 mr-1" /> 
                  {i18n.language === 'fr' ? 'Évaluer' : 'Review'}
                </Button>
              )}
              
              {/* Add photos button for completed bookings */}
              {booking.status === 'completed' && !booking.photos_added && (
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setShowPhotoModal(true)}
                  className="text-purple-600 border-purple-200 hover:bg-purple-50"
                  data-testid="add-photos-btn"
                >
                  <Camera className="w-4 h-4 mr-1" />
                  {i18n.language === 'fr' ? 'Photos' : 'Photos'}
                </Button>
              )}
              
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={() => navigate(`/experience/${booking.experience_id}`)}
              >
                <Eye className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Complete Experience Confirmation Modal */}
      <Dialog open={showCompleteModal} onOpenChange={setShowCompleteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {i18n.language === 'fr' ? 'Confirmer la fin de l\'expérience' : 'Confirm experience completion'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-muted-foreground">
              {i18n.language === 'fr' 
                ? 'En confirmant, vous indiquez que l\'expérience s\'est bien déroulée. Le paiement sera libéré au guide après 48h.'
                : 'By confirming, you indicate the experience went well. Payment will be released to the guide after 48h.'}
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
              <AlertTriangle className="w-4 h-4 inline mr-2" />
              {i18n.language === 'fr' 
                ? 'Vous avez 48h pour signaler un problème après confirmation.'
                : 'You have 48h to report any issue after confirmation.'}
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowCompleteModal(false)}>
                {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
              </Button>
              <Button 
                onClick={handleCompleteExperience}
                disabled={actionLoading}
                className="bg-green-600 hover:bg-green-700"
              >
                {actionLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                {i18n.language === 'fr' ? 'Confirmer' : 'Confirm'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Review Modal */}
      <ReviewModal 
        open={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        booking={booking}
        onSuccess={() => {
          setShowReviewModal(false);
          onRefresh();
        }}
      />

      {/* Community Photos Modal */}
      <CommunityPhotosModal
        open={showPhotoModal}
        onClose={() => setShowPhotoModal(false)}
        booking={booking}
        onSuccess={() => {
          setShowPhotoModal(false);
          onRefresh();
          toast.success(i18n.language === 'fr' 
            ? 'Photos ajoutées avec succès !' 
            : 'Photos added successfully!');
        }}
      />
    </motion.div>
  );
};

const ExperienceRow = ({ experience, onRefresh }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  
  const handleDelete = async () => {
    if (window.confirm('Supprimer cette expérience ?')) {
      try {
        await experiencesApi.delete(experience.experience_id);
        toast.success('Expérience supprimée');
        onRefresh();
      } catch (error) {
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const title = i18n.language === 'en' && experience.title_en ? experience.title_en : experience.title;

  return (
    <div className="bg-card rounded-xl border p-4 flex items-center gap-4" data-testid={`experience-row-${experience.experience_id}`}>
      <img 
        src={experience.images?.[0] || 'https://images.unsplash.com/photo-1552842491-a128bc045456?w=200'}
        alt={title}
        className="w-20 h-20 rounded-lg object-cover"
      />
      <div className="flex-1">
        <h3 className="font-semibold">{title}</h3>
        <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
          <span className="flex items-center gap-1">
            <MapPin className="w-3 h-3" /> {experience.location}
          </span>
          <span>{experience.price}€</span>
          {experience.rating > 0 && (
            <span className="flex items-center gap-1">
              <Star className="w-3 h-3 text-yellow-500" /> {experience.rating.toFixed(1)}
            </span>
          )}
        </div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="ghost" onClick={() => navigate(`/experience/${experience.experience_id}`)}>
          <Eye className="w-4 h-4" />
        </Button>
        <Button size="sm" variant="ghost" className="text-destructive" onClick={handleDelete}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

const CreateExperienceModal = ({ open, onClose, onSuccess }) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    title_en: '',
    description: '',
    description_en: '',
    categories: [],  // Multiple categories
    location: '',
    price: '',
    duration_hours: '',
    max_participants: '10',
    includes_vehicle: false,
    includes_accommodation: false,
    vehicle_price: '0',
    accommodation_price: '0',
    images: [],
    experience_type: 'experience',  // 'experience' (paid) or 'meetup' (free)
    cancellation_policy: 'flexible'  // Default to flexible
  });

  const categories = [
    { id: 'adventure', name: 'Aventure & Nature' },
    { id: 'fun', name: 'Fun' },
    { id: 'food', name: 'Gastronomie' },
    { id: 'culture', name: 'Culture & Histoire' },
    { id: 'sport', name: 'Sport' },
    { id: 'healing', name: 'Healing & Retraite' },
    { id: 'nightlife', name: 'Vie nocturne' },
    { id: 'vanlife', name: 'Vanlife' },
    { id: 'artisanat', name: 'Artisanat' },
    { id: 'local_events', name: 'Événements locaux' }
  ];

  const isFreeExperience = formData.experience_type === 'meetup' || (formData.experience_type === 'experience' && parseFloat(formData.price || 0) === 0);
  const isEvent = formData.experience_type === 'event';

  const toggleCategory = (catId) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(catId)
        ? prev.categories.filter(c => c !== catId)
        : [...prev.categories, catId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.categories.length === 0) {
      toast.error(i18n.language === 'fr' ? 'Sélectionnez au moins une catégorie' : 'Select at least one category');
      return;
    }
    
    setLoading(true);
    
    const priceValue = formData.experience_type === 'event' ? 0 : parseFloat(formData.price || 0);
    const isFree = priceValue === 0;
    
    try {
      await experiencesApi.create({
        ...formData,
        price: priceValue,
        is_free: isFree,
        members_only: isFree && formData.experience_type !== 'event', // Free experiences are members only (except events)
        duration_hours: parseFloat(formData.duration_hours),
        max_participants: parseInt(formData.max_participants),
        vehicle_price: parseFloat(formData.vehicle_price) || 0,
        accommodation_price: parseFloat(formData.accommodation_price) || 0
      });
      
      const successMessages = {
        experience: i18n.language === 'fr' ? 'Expérience créée !' : 'Experience created!',
        meetup: i18n.language === 'fr' ? 'Rencontre créée !' : 'Meetup created!',
        event: i18n.language === 'fr' ? 'Événement créé !' : 'Event created!'
      };
      toast.success(successMessages[formData.experience_type]);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('dashboard.create_experience')}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-sm font-medium">Titre (FR) *</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                data-testid="exp-title-input"
              />
            </div>
            <div className="col-span-2">
              <label className="text-sm font-medium">Titre (EN)</label>
              <Input
                value={formData.title_en}
                onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Description (FR) *</label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              required
              data-testid="exp-description-input"
            />
          </div>

          <div>
            <label className="text-sm font-medium">Description (EN)</label>
            <Textarea
              value={formData.description_en}
              onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
              rows={4}
            />
          </div>

          {/* Categories Selection - Multiple Choice */}
          <div className="space-y-3">
            <label className="text-sm font-medium">
              {i18n.language === 'fr' ? 'Catégories *' : 'Categories *'}
              <span className="text-muted-foreground font-normal ml-2">
                ({i18n.language === 'fr' ? 'sélectionnez une ou plusieurs' : 'select one or more'})
              </span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              {categories.map((cat) => (
                <label
                  key={cat.id}
                  className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    formData.categories.includes(cat.id)
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                  data-testid={`category-checkbox-${cat.id}`}
                >
                  <Checkbox
                    checked={formData.categories.includes(cat.id)}
                    onCheckedChange={() => toggleCategory(cat.id)}
                  />
                  <span className="text-sm">{cat.name}</span>
                </label>
              ))}
            </div>
            {formData.categories.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {i18n.language === 'fr' ? 'Sélectionnées : ' : 'Selected: '}
                {formData.categories.map(c => categories.find(cat => cat.id === c)?.name).join(', ')}
              </p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium">Lieu *</label>
            <Input
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="Paris, France"
              required
              data-testid="exp-location-input"
            />
          </div>

          {/* Experience Type Selection */}
          <div className="space-y-3">
            <label className="text-sm font-medium">
              {i18n.language === 'fr' ? 'Type de publication *' : 'Publication type *'}
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Expérience guidée */}
              <label
                className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  formData.experience_type === 'experience'
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="experience_type"
                  value="experience"
                  checked={formData.experience_type === 'experience'}
                  onChange={(e) => setFormData({ ...formData, experience_type: e.target.value })}
                  className="mt-1"
                />
                <div>
                  <div className="flex items-center gap-2">
                    <Compass className="w-5 h-5 text-primary" />
                    <span className="font-semibold text-sm">
                      {i18n.language === 'fr' ? 'Expérience' : 'Experience'}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {i18n.language === 'fr' 
                      ? 'Fixe ton prix librement. Gratuit = accès membres.'
                      : 'Set your price freely. Free = members only.'}
                  </p>
                  <span className="text-xs text-green-600 mt-2 inline-block font-medium">
                    {i18n.language === 'fr' ? 'Commission 15%' : '15% commission'}
                  </span>
                </div>
              </label>

              {/* Rencontre communautaire (gratuite) */}
              <label
                className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  formData.experience_type === 'meetup'
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="experience_type"
                  value="meetup"
                  checked={formData.experience_type === 'meetup'}
                  onChange={(e) => setFormData({ ...formData, experience_type: e.target.value, price: '0' })}
                  className="mt-1"
                />
                <div>
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-purple-600" />
                    <span className="font-semibold text-sm">
                      {i18n.language === 'fr' ? 'Rencontre' : 'Meetup'}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {i18n.language === 'fr' 
                      ? 'Entre passionnés. Vanlife, randos, échanges...'
                      : 'Between enthusiasts. Vanlife, hikes, exchanges...'}
                  </p>
                  <span className="text-xs text-purple-600 mt-2 inline-block font-medium">
                    🔒 {i18n.language === 'fr' ? 'Gratuit • Membres uniquement' : 'Free • Members only'}
                  </span>
                </div>
              </label>

              {/* Événement local (gratuit) */}
              <label
                className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  formData.experience_type === 'event'
                    ? 'border-amber-500 bg-amber-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="experience_type"
                  value="event"
                  checked={formData.experience_type === 'event'}
                  onChange={(e) => setFormData({ ...formData, experience_type: e.target.value, price: '0' })}
                  className="mt-1"
                />
                <div>
                  <div className="flex items-center gap-2">
                    <CalendarDays className="w-5 h-5 text-amber-600" />
                    <span className="font-semibold text-sm">
                      {i18n.language === 'fr' ? 'Événement local' : 'Local Event'}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {i18n.language === 'fr' 
                      ? 'Festival, marché, fête locale à faire découvrir.'
                      : 'Festival, market, local celebration to share.'}
                  </p>
                  <span className="text-xs text-amber-600 mt-2 inline-block font-medium">
                    {i18n.language === 'fr' ? 'Gratuit • Visible par tous' : 'Free • Visible to all'}
                  </span>
                </div>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            {formData.experience_type === 'experience' && (
              <div>
                <label className="text-sm font-medium">
                  {i18n.language === 'fr' ? 'Prix (€) *' : 'Price (€) *'}
                </label>
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  min="0"
                  step="1"
                  placeholder="0"
                  required
                  data-testid="exp-price-input"
                />
                {formData.price === '0' || formData.price === '' ? (
                  <p className="text-xs text-purple-600 mt-1 flex items-center gap-1">
                    <span>🔒</span>
                    {i18n.language === 'fr' ? 'Gratuit = Accès membres uniquement' : 'Free = Members only access'}
                  </p>
                ) : (
                  <p className="text-xs text-green-600 mt-1">
                    {i18n.language === 'fr' ? 'Commission 15% • Accès pour tous' : '15% commission • Open to all'}
                  </p>
                )}
              </div>
            )}
            {formData.experience_type === 'meetup' && (
              <div className="flex flex-col justify-end">
                <div className="bg-purple-50 text-purple-700 rounded-lg px-3 py-2 text-sm font-medium">
                  {i18n.language === 'fr' ? 'Gratuit' : 'Free'}
                </div>
                <p className="text-xs text-purple-600 mt-1 flex items-center gap-1">
                  <span>🔒</span>
                  {i18n.language === 'fr' ? 'Accès membres uniquement' : 'Members only'}
                </p>
              </div>
            )}
            {formData.experience_type === 'event' && (
              <div className="flex flex-col justify-end">
                <div className="bg-amber-50 text-amber-700 rounded-lg px-3 py-2 text-sm font-medium">
                  {i18n.language === 'fr' ? 'Gratuit' : 'Free'}
                </div>
                <p className="text-xs text-amber-600 mt-1">
                  {i18n.language === 'fr' ? 'Visible par tous' : 'Visible to everyone'}
                </p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium">Durée (heures) *</label>
              <Input
                type="number"
                value={formData.duration_hours}
                onChange={(e) => setFormData({ ...formData, duration_hours: e.target.value })}
                min="0.5"
                step="0.5"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium">Max participants</label>
              <Input
                type="number"
                value={formData.max_participants}
                onChange={(e) => setFormData({ ...formData, max_participants: e.target.value })}
                min="1"
              />
            </div>
          </div>

          {/* Cancellation Policy - Platform-wide */}
          <div className="space-y-3">
            <label className="text-sm font-medium">
              {i18n.language === 'fr' ? 'Politique d\'annulation XPerience' : 'XPerience Cancellation Policy'}
            </label>
            <div className="p-4 rounded-lg border-2 border-primary/30 bg-orange-50">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-5 h-5 text-primary" />
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-800">
                    {i18n.language === 'fr' 
                      ? 'Politique uniforme pour toutes les expériences'
                      : 'Uniform policy for all experiences'}
                  </p>
                  <ul className="text-xs text-gray-600 space-y-1">
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-500"></span>
                      {i18n.language === 'fr' 
                        ? 'Plus de 24h avant : Remboursement 100%'
                        : 'More than 24h before: 100% refund'}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-red-500"></span>
                      {i18n.language === 'fr' 
                        ? 'Moins de 24h avant : Pas de remboursement'
                        : 'Less than 24h before: No refund'}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                      {i18n.language === 'fr' 
                        ? 'Météo / Force majeure : Remboursement 100% ou report'
                        : 'Weather / Force majeure: 100% refund or reschedule'}
                    </li>
                  </ul>
                  <p className="text-xs text-gray-500 italic mt-2">
                    {i18n.language === 'fr' 
                      ? 'Cette politique est appliquée automatiquement à toutes vos expériences.'
                      : 'This policy is automatically applied to all your experiences.'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Checkbox
                id="vehicle"
                checked={formData.includes_vehicle}
                onCheckedChange={(checked) => setFormData({ ...formData, includes_vehicle: checked })}
              />
              <label htmlFor="vehicle" className="text-sm">Propose un véhicule</label>
              {formData.includes_vehicle && (
                <Input
                  type="number"
                  value={formData.vehicle_price}
                  onChange={(e) => setFormData({ ...formData, vehicle_price: e.target.value })}
                  placeholder="Prix véhicule"
                  className="w-32"
                  min="0"
                />
              )}
            </div>
            <div className="flex items-center gap-3">
              <Checkbox
                id="accommodation"
                checked={formData.includes_accommodation}
                onCheckedChange={(checked) => setFormData({ ...formData, includes_accommodation: checked })}
              />
              <label htmlFor="accommodation" className="text-sm">Propose un hébergement</label>
              {formData.includes_accommodation && (
                <Input
                  type="number"
                  value={formData.accommodation_price}
                  onChange={(e) => setFormData({ ...formData, accommodation_price: e.target.value })}
                  placeholder="Prix hébergement"
                  className="w-32"
                  min="0"
                />
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={loading} data-testid="create-exp-submit">
              {loading ? t('common.loading') : t('common.save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ReviewModal = ({ open, onClose, booking, onSuccess }) => {
  const { t } = useTranslation();
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await reviewsApi.create({
        booking_id: booking.booking_id,
        rating,
        comment
      });
      toast.success('Avis publié !');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Évaluer votre expérience</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="text-center">
            <p className="text-muted-foreground mb-4">{booking.experience_title}</p>
            <InteractiveStarRating value={rating} onChange={setRating} />
          </div>
          
          <div>
            <label className="text-sm font-medium">Votre avis</label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Partagez votre expérience..."
              rows={4}
              required
              data-testid="review-comment"
            />
          </div>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={loading} data-testid="submit-review">
              {loading ? t('common.loading') : 'Publier'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};


// Community Photos Modal Component
const CommunityPhotosModal = ({ open, onClose, booking, onSuccess }) => {
  const { i18n } = useTranslation();
  const [photoUrls, setPhotoUrls] = useState(['']);
  const [caption, setCaption] = useState('');
  const [loading, setLoading] = useState(false);

  const addPhotoField = () => {
    if (photoUrls.length < 5) {
      setPhotoUrls([...photoUrls, '']);
    }
  };

  const updatePhotoUrl = (index, value) => {
    const newUrls = [...photoUrls];
    newUrls[index] = value;
    setPhotoUrls(newUrls);
  };

  const removePhotoField = (index) => {
    if (photoUrls.length > 1) {
      setPhotoUrls(photoUrls.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validUrls = photoUrls.filter(url => url.trim() !== '');
    if (validUrls.length === 0) {
      toast.error(i18n.language === 'fr' ? 'Ajoutez au moins une photo' : 'Add at least one photo');
      return;
    }

    setLoading(true);
    try {
      // Helper to get API URL - uses current origin in production
      const getApiUrl = () => {
        if (typeof window !== 'undefined' && window.location.origin.includes('xperience-guides.com')) {
          return window.location.origin;
        }
        return process.env.REACT_APP_BACKEND_URL || window.location.origin;
      };
      const API_URL = getApiUrl();
      const token = localStorage.getItem('vgt_token');
      
      const response = await fetch(`${API_URL}/api/experiences/${booking.experience_id}/community-photos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          experience_id: booking.experience_id,
          booking_id: booking.booking_id,
          photo_urls: validUrls,
          caption: caption
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error');
      }

      onSuccess();
    } catch (error) {
      toast.error(error.message || (i18n.language === 'fr' ? 'Erreur' : 'Error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-purple-600" />
            {i18n.language === 'fr' ? 'Partagez vos photos' : 'Share your photos'}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {i18n.language === 'fr' 
              ? 'Partagez vos meilleurs moments de cette expérience avec la communauté !'
              : 'Share your best moments from this experience with the community!'}
          </p>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm text-purple-800">
            📸 {i18n.language === 'fr' 
              ? `Expérience : ${booking.experience_title}`
              : `Experience: ${booking.experience_title}`}
          </div>

          <div className="space-y-3">
            <label className="text-sm font-medium">
              {i18n.language === 'fr' ? 'URLs des photos' : 'Photo URLs'}
              <span className="text-muted-foreground font-normal ml-1">(max 5)</span>
            </label>
            
            {photoUrls.map((url, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  value={url}
                  onChange={(e) => updatePhotoUrl(index, e.target.value)}
                  placeholder={i18n.language === 'fr' ? 'https://example.com/photo.jpg' : 'https://example.com/photo.jpg'}
                  className="flex-1"
                  data-testid={`photo-url-${index}`}
                />
                {photoUrls.length > 1 && (
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm"
                    onClick={() => removePhotoField(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <XCircle className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
            
            {photoUrls.length < 5 && (
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={addPhotoField}
                className="w-full"
              >
                <Plus className="w-4 h-4 mr-1" />
                {i18n.language === 'fr' ? 'Ajouter une photo' : 'Add photo'}
              </Button>
            )}
          </div>

          <div>
            <label className="text-sm font-medium">
              {i18n.language === 'fr' ? 'Légende (optionnel)' : 'Caption (optional)'}
            </label>
            <Textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder={i18n.language === 'fr' ? 'Décrivez votre expérience...' : 'Describe your experience...'}
              rows={2}
              data-testid="photo-caption"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="submit-photos"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Camera className="w-4 h-4 mr-2" />
                  {i18n.language === 'fr' ? 'Publier' : 'Publish'}
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};



// Verification Card Component
const VerificationCard = ({ user }) => {
  const { i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  
  const isVerified = user?.verified;
  const isPending = user?.verification_requested && !isVerified;
  const isRejected = user?.verification_rejected;

  const handleRequestVerification = async (documentUrl) => {
    setLoading(true);
    try {
      // Helper to get API URL - uses current origin in production
      const getApiUrl = () => {
        if (typeof window !== 'undefined' && window.location.origin.includes('xperience-guides.com')) {
          return window.location.origin;
        }
        return process.env.REACT_APP_BACKEND_URL || window.location.origin;
      };
      const API_URL = getApiUrl();
      const token = localStorage.getItem('vgt_token');
      
      await fetch(`${API_URL}/api/guides/request-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          document_type: 'id_card',
          document_url: documentUrl
        })
      });
      
      toast.success(i18n.language === 'fr' 
        ? 'Demande de vérification envoyée !' 
        : 'Verification request sent!');
      setShowModal(false);
      window.location.reload();
    } catch (error) {
      toast.error(i18n.language === 'fr' ? 'Erreur' : 'Error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-6 rounded-2xl border-2 ${
        isVerified 
          ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200' 
          : isPending
            ? 'bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-200'
            : 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200'
      }`}
    >
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
          isVerified ? 'bg-green-100' : isPending ? 'bg-yellow-100' : 'bg-blue-100'
        }`}>
          {isVerified ? (
            <BadgeCheck className="w-6 h-6 text-green-600" />
          ) : isPending ? (
            <Clock className="w-6 h-6 text-yellow-600" />
          ) : (
            <ShieldCheck className="w-6 h-6 text-blue-600" />
          )}
        </div>
        
        <div className="flex-1">
          <h3 className="font-bold text-lg mb-1">
            {isVerified 
              ? (i18n.language === 'fr' ? 'Guide Vérifié' : 'Verified Guide')
              : isPending
                ? (i18n.language === 'fr' ? 'Vérification en cours' : 'Verification Pending')
                : (i18n.language === 'fr' ? 'Faites-vous vérifier' : 'Get Verified')
            }
          </h3>
          
          <p className="text-sm text-gray-600 mb-3">
            {isVerified 
              ? (i18n.language === 'fr' 
                  ? 'Votre profil est vérifié. Les voyageurs vous font confiance !' 
                  : 'Your profile is verified. Travelers trust you!')
              : isPending
                ? (i18n.language === 'fr' 
                    ? 'Nous examinons votre demande (24-48h)' 
                    : 'We are reviewing your request (24-48h)')
                : (i18n.language === 'fr' 
                    ? 'Obtenez le badge vérifié pour rassurer les voyageurs' 
                    : 'Get the verified badge to reassure travelers')
            }
          </p>
          
          {isVerified && (
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                <BadgeCheck className="w-4 h-4" />
                {i18n.language === 'fr' ? 'Vérifié' : 'Verified'}
              </span>
            </div>
          )}
          
          {isPending && (
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium">
                <Loader2 className="w-4 h-4 animate-spin" />
                {i18n.language === 'fr' ? 'En attente' : 'Pending'}
              </span>
            </div>
          )}
          
          {!isVerified && !isPending && (
            <Dialog open={showModal} onOpenChange={setShowModal}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                  <Upload className="w-4 h-4 mr-2" />
                  {i18n.language === 'fr' ? 'Demander la vérification' : 'Request Verification'}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {i18n.language === 'fr' ? 'Vérification d\'identité' : 'Identity Verification'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    {i18n.language === 'fr' 
                      ? 'Pour obtenir le badge vérifié, veuillez fournir un lien vers une photo de votre pièce d\'identité (carte d\'identité ou passeport).'
                      : 'To get the verified badge, please provide a link to a photo of your ID (ID card or passport).'}
                  </p>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm text-yellow-800">
                      {i18n.language === 'fr' 
                        ? '🔒 Vos données sont sécurisées et ne seront utilisées que pour la vérification.'
                        : '🔒 Your data is secure and will only be used for verification.'}
                    </p>
                  </div>
                  <Input 
                    id="document-url"
                    placeholder={i18n.language === 'fr' ? 'URL de votre document...' : 'URL of your document...'}
                    data-testid="verification-document-url"
                  />
                  <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={() => setShowModal(false)}>
                      {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
                    </Button>
                    <Button 
                      onClick={() => {
                        const url = document.getElementById('document-url').value;
                        if (url) handleRequestVerification(url);
                      }}
                      disabled={loading}
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (i18n.language === 'fr' ? 'Envoyer' : 'Submit')}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Local Events Tab Component for Guides
const LocalEventsTab = ({ experiences, linkedEventIds, onPromoteEvent, onOpenLinkModal }) => {
  const { i18n } = useTranslation();
  
  // Filter user's events/retreats
  const userEvents = experiences.filter(exp => {
    const categories = exp.categories || [exp.category];
    return categories.some(cat => ['local_events', 'healing'].includes(cat));
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">
            {i18n.language === 'fr' ? 'Événements & Retraites' : 'Events & Retreats'}
          </h2>
          <p className="text-gray-600 mt-1">
            {i18n.language === 'fr' 
              ? 'Gérez vos événements et liez des événements locaux à votre profil'
              : 'Manage your events and link local events to your profile'}
          </p>
        </div>
        
        <Button 
          onClick={onOpenLinkModal}
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="link-events-btn"
        >
          <Link2 className="w-4 h-4 mr-2" />
          {i18n.language === 'fr' ? 'Lier des événements' : 'Link events'}
        </Button>
      </div>

      {/* Linked Events Info */}
      {linkedEventIds.length > 0 && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <Link2 className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="font-medium text-purple-900">
                {linkedEventIds.length} {i18n.language === 'fr' ? 'événement(s) lié(s) à votre profil' : 'event(s) linked to your profile'}
              </p>
              <p className="text-sm text-purple-700">
                {i18n.language === 'fr' 
                  ? 'Les voyageurs verront ces événements sur votre page de profil'
                  : 'Travelers will see these events on your profile page'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* User's Events */}
      <div>
        <h3 className="text-lg font-semibold mb-4">
          {i18n.language === 'fr' ? 'Vos événements' : 'Your events'}
        </h3>
        
        {userEvents.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
            <CalendarDays className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500 mb-2">
              {i18n.language === 'fr' 
                ? 'Vous n\'avez pas encore d\'événement ou de retraite'
                : 'You don\'t have any events or retreats yet'}
            </p>
            <p className="text-sm text-gray-400">
              {i18n.language === 'fr' 
                ? 'Créez une expérience avec la catégorie "Événements locaux" ou "Bien-être"'
                : 'Create an experience with category "Local Events" or "Healing"'}
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {userEvents.map((event) => (
              <div 
                key={event.experience_id}
                className="bg-white rounded-xl border overflow-hidden hover:shadow-lg transition-shadow"
                data-testid={`user-event-${event.experience_id}`}
              >
                <div className="relative h-40">
                  <img 
                    src={event.images?.[0] || 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=400'}
                    alt={event.title}
                    className="w-full h-full object-cover"
                  />
                  {event.is_featured && (
                    <div className="absolute top-2 left-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                      <Star className="w-3 h-3" fill="white" />
                      {i18n.language === 'fr' ? 'À la une' : 'Featured'}
                    </div>
                  )}
                  <div className="absolute top-2 right-2 bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                    {event.categories?.includes('healing') || event.category === 'healing'
                      ? (i18n.language === 'fr' ? 'Retraite' : 'Retreat')
                      : (i18n.language === 'fr' ? 'Événement' : 'Event')}
                  </div>
                </div>
                
                <div className="p-4">
                  <h4 className="font-semibold text-gray-900 mb-2 line-clamp-2">{event.title}</h4>
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                    <MapPin className="w-4 h-4" />
                    <span className="truncate">{event.location}</span>
                  </div>
                  
                  {!event.is_featured && (
                    <Button 
                      onClick={() => onPromoteEvent(event)}
                      size="sm"
                      className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
                      data-testid={`promote-btn-${event.experience_id}`}
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      {i18n.language === 'fr' ? 'Mettre en avant' : 'Promote'}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


export default DashboardPage;
